// aiChat.fetchers.ts — the single thin, typed fetcher over the Rheora AI
// chatbot host, plus the api client it speaks through.
//
// Layer rules (CLAUDE.md): fetchers are thin and typed; pages NEVER call them,
// only the `aiChat.queries.ts` hooks do. The response is validated at the
// boundary through `ChatReplySchema`, so contract drift surfaces as
// `ApiError { kind: 'validation' }` here rather than as `undefined` deep in a
// render.
//
// ─── WHY THE CLIENT IS BUILT HERE AND NOT IN `lib/apiClient.ts` ─────────────
//
// Every client in `lib/apiClient.ts` is one of OUR backend services. This host
// is not: it is a separate FastAPI application (`rheora-crm-chatbot`) on its
// own origin that today accepts UNAUTHENTICATED requests and takes its tenant
// from the request BODY. Both of those are expected to change — the schema's
// own description calls the body-borne `tenant_id` *"temporary"*.
//
// Keeping the client in this file means the move to an authenticated proxy (a
// real bearer, the tenant back in a header or a token claim, `onUnauthorized`
// wired to the shared handler) is a ONE-FILE change, and that nothing else in
// the CRM can accidentally acquire a credential-free client by importing from
// the shared module.

import { createServiceApiClient, type ServiceApiClient } from '@fluxora/api-client';

import { env } from '../../../config/env';
import { CHAT_TIMEOUT_MS } from '../constants';
import {
  ChatReplySchema,
  type ChatReply,
  type ChatRequest,
} from '../schemas/chat.schema';

/**
 * The one route this module calls. Spelled out in full rather than composed
 * from a `const PREFIX`, for the same reason as GMO's fetchers: the version
 * segment is part of the route's identity, not a configurable knob, and a wrong
 * guess produces a 404 that reads as "the feature is off".
 */
const CHAT_PATH = '/api/v1/chat';

/**
 * The chatbot host client.
 *
 * Every line below is deliberate, and every one of them is a trap if changed.
 */
const aiChatApi: ServiceApiClient = createServiceApiClient({
  // Supplied by `config/env.ts` (`VITE_AI_CHAT_API_URL`). A foreign origin, so
  // it is always an ABSOLUTE url — deliberately with no same-origin fallback,
  // which would quietly aim these calls at the CRM's own ingress and turn a
  // missing configuration into a stream of 404s against our own API.
  //
  // Unconfigured, it is the empty string, and the factory's `new URL(path, '')`
  // throws on the first ask. That is the intended failure: loud, immediate and
  // attributable, rather than a panel that looks alive and answers nothing. The
  // panel's own feature gate — not this client — is what keeps the surface out
  // of an environment where the host is not deployed.
  baseUrl: env.aiChatApiUrl,

  // Send ONLY Accept / Accept-Language / Content-Type, suppressing
  // `x-tenant-id`, `x-country-code`, `x-request-id` and `x-actor-user-id`.
  //
  // NOT because CORS forces our hand — this host reflects whatever headers the
  // preflight asks for, so it would happily accept them. The reason is that
  // shipping our tenant id, our acting operator's id and (below) a CRM bearer
  // to an UNAUTHENTICATED third-party host is precisely the thing we are
  // avoiding: anything sent here is readable by anyone who can reach the host
  // and lands in its logs.
  //
  // Cost of the choice: it also suppresses `x-request-id`, so there is NO
  // client-generated correlation id on these calls. `run_id` from the reply is
  // the only handle that ties a support ticket to a server-side run, which is
  // why it is a REQUIRED field in the schema and why "Copy reference" exists.
  omitContextHeaders: true,

  // No `Authorization` header at all — the factory only sets it when this
  // returns a truthy token (`createApiClient.ts:222-223`). Returning `null` is
  // therefore not "an empty bearer", it is no bearer. Do not be tempted to pass
  // the CRM's session token "just in case the host starts checking": a token
  // sent to a host that does not verify it is a token disclosed for nothing.
  getAuthToken: () => null,

  // Required by `ServiceApiClientConfig`, never sent (`omitContextHeaders`
  // suppresses the header it feeds). The REAL tenant travels in the request
  // BODY as `tenant_id`, supplied by the caller from the verified session
  // claim — see `ChatRequestSchema`. An empty string here is the honest value:
  // this client has no tenant of its own.
  getTenantId: () => '',

  // Same: required by the config type, suppressed by `omitContextHeaders`.
  // 'US' rather than '' only so that a future removal of `omitContextHeaders`
  // cannot silently start sending a header the Pricing-style 400 would reject.
  getCountryCode: () => 'US',

  // Same: required, suppressed, and additionally the factory only sets
  // `x-actor-user-id` when it is truthy. The operator's identity is not this
  // host's business.
  getActorUserId: () => null,

  // 25s. The factory default of 15s is under 2x headroom over the worst
  // measured response (7.7s) and the server performs its OWN internal retries
  // (`retry_count` is part of the contract), so a query rebuild on a cold model
  // can plausibly cross 15s. See `constants.ts` for the measurements.
  defaultTimeoutMs: CHAT_TIMEOUT_MS,

  // `1` means NO RETRY, not "one retry". The factory's loop is
  // `while (attempt < Math.max(1, maxRetries))` and its inner retry guard is
  // `attempt < maxRetries`, so at 1 the body runs exactly once and never
  // repeats. This is the single most important line in the file: the DEFAULT of
  // 3 would turn one 25s timeout into a silent 75s hang with no progress
  // feedback, and would bill the LLM three times for one question. On this
  // surface a retry is always a user gesture — see `AiFailure` /
  // `AiRetryability` — never an invisible one.
  maxRetries: 1,

  // Deliberately NO `onUnauthorized`, following the `portalApi` precedent in
  // `lib/apiClient.ts`. Every client built by `build()` shares a global 401
  // handler whose contract is "force navigation to /login". That is right for
  // our own services and wrong here: a 401/403 from this host means "the
  // chatbot does not accept me", not "your CRM session expired", and wiring it
  // up would let a chatbot misconfiguration sign operators out mid-task.
  //
  // This is already safe by construction — `lib/sessionExpiry.ts:122-150`
  // independently requires `isAccessTokenExpired()` before ending a session —
  // so this omission needs no workaround anywhere else. A foreign auth failure
  // surfaces as a failed ask and nothing more.
});

/**
 * `POST /api/v1/chat` — ask one question.
 *
 * `signal` is threaded straight through so the Stop control can cancel an
 * in-flight ask. Note the shared factory surfaces an externally-aborted request
 * as `ApiError { kind: 'network' }`, byte-identical to a genuine connectivity
 * failure; distinguishing the two is the state machine's job (it records
 * `cancelledByUser` BEFORE calling `abort()`), not this layer's.
 */
export function postChat(body: ChatRequest, signal?: AbortSignal): Promise<ChatReply> {
  return aiChatApi.post<ChatReply>(CHAT_PATH, body, { schema: ChatReplySchema, signal });
}
